import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row } from 'reactstrap';
import notify from '../../../../../../../utils/notify';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import ClauseCard from '../../../../../../../shared/offer-domain/clause';
import {
  SectionHeader,
  SectionAlert,
  SectionCard,
} from '../../../../../../../shared/offer-domain/ui';
import useClarificationClauseResponses from '../../../../../../../shared/offer-domain/respond/useClarificationClauseResponses';
import useSubmittedBidder from '../../../../../hooks/useSubmittedBidder';
import { EmptyState } from '../../../../../../../Components/Common/ListStates';
import {
  getOwnClauseResponse,
  getLastOwnEditableResponse,
  getVisibleProcurementClauses,
  hasClauseResponse,
  isBidderClauseConfirmed,
  toClauseResponseList,
  needsIcReplyOnEvaluatorThread,
  needsTeAgreeOnLastCounter,
  hasTeClarificationRespondReady,
  createLockRespondHandler,
} from '../../../../../../../shared/offer-domain/respond';

const EVALUATOR_FORM_MAIN = {
  statusOfferingWordingFlag: false,
};

const ClauseDetailSection = ({
  procurement,
  procurementId,
  offerId,
  procurementMitraId: procurementMitraIdProp = null,
  isEvaluatorView = true,
  isRespondClosed = false,
  highlightArmed = false,
  onPendingRespondIds,
}) => {
  const token = getAuthToken();
  const { items } = useSubmittedBidder(procurementId, {
    enabled: !procurementMitraIdProp,
  });
  const [draftNotes, setDraftNotes] = useState({});
  const [expandedClauses, setExpandedClauses] = useState({});

  const bidder = useMemo(() => {
    if (procurementMitraIdProp) {
      return { id: procurementMitraIdProp };
    }
    return (
      items.find(
        item =>
          String(item.idOffer) === String(offerId) ||
          String(item.id) === String(offerId)
      ) ?? null
    );
  }, [items, offerId, procurementMitraIdProp]);

  const visibleClauses = useMemo(
    () => getVisibleProcurementClauses(procurement),
    [procurement]
  );

  const clauseDetailIds = useMemo(
    () => visibleClauses.map(clause => clause.id).filter(Boolean),
    [visibleClauses]
  );

  const {
    clauseResponses,
    isLoading: isClauseResponsesLoading,
    isSaving: isClauseResponsesSaving,
    saveResponse: saveClauseResponse,
    deleteResponse: deleteClauseResponse,
    lockResponse,
  } = useClarificationClauseResponses(
    token,
    bidder?.id,
    clauseDetailIds,
    isEvaluatorView,
    procurementId
  );

  const confirmationStats = useMemo(() => {
    const mandatoryClauses = visibleClauses.filter(
      clause => clause.isShowToDMUT === true
    );
    const optionalClauses = visibleClauses.filter(
      clause => clause.isShowToDMUT !== true
    );
    const confirmedCount = visibleClauses.filter(clause =>
      isBidderClauseConfirmed(clauseResponses[clause.id])
    ).length;
    const mandatoryConfirmedCount = mandatoryClauses.filter(clause =>
      isBidderClauseConfirmed(clauseResponses[clause.id])
    ).length;
    const optionalConfirmedCount = optionalClauses.filter(clause =>
      isBidderClauseConfirmed(clauseResponses[clause.id])
    ).length;
    return {
      confirmedCount,
      total: visibleClauses.length,
      mandatoryConfirmedCount,
      mandatoryTotal: mandatoryClauses.length,
      optionalConfirmedCount,
      optionalTotal: optionalClauses.length,
    };
  }, [visibleClauses, clauseResponses]);

  const handleLockResponse = createLockRespondHandler(lockResponse);

  useEffect(() => {
    setDraftNotes({});
    setExpandedClauses({});
  }, [bidder?.id, offerId]);

  useEffect(() => {
    if (isClauseResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(clauseResponses).forEach(([clauseId, responses]) => {
      const lastEditable = getLastOwnEditableResponse(responses, {
        isEvaluator: isEvaluatorView,
      });
      if (lastEditable && hasClauseResponse(lastEditable?.respondNotes)) {
        expanded[clauseId] = true;
        drafts[clauseId] = lastEditable.respondNotes;
      } else if (
        toClauseResponseList(responses).some(item =>
          hasClauseResponse(item?.respondNotes)
        )
      ) {
        expanded[clauseId] = true;
      }
    });

    setExpandedClauses(expanded);
    setDraftNotes(drafts);
  }, [clauseResponses, isClauseResponsesLoading, isEvaluatorView, bidder?.id]);

  const isPendingThread = useCallback(
    list =>
      isEvaluatorView
        ? needsTeAgreeOnLastCounter(list)
        : needsIcReplyOnEvaluatorThread(list),
    [isEvaluatorView]
  );

  useEffect(() => {
    if (!highlightArmed) return;
    setExpandedClauses(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(clauseResponses).forEach(([clauseId, list]) => {
        if (isPendingThread(list) && !next[clauseId]) {
          next[clauseId] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [highlightArmed, clauseResponses, isPendingThread]);

  useEffect(() => {
    if (isClauseResponsesLoading) return;
    const ids = Object.entries(clauseResponses)
      .filter(([, list]) => isPendingThread(list))
      .map(([clauseId]) => `clause-${clauseId}`);
    const hasClarificationRespond = Object.values(clauseResponses).some(
      hasTeClarificationRespondReady
    );
    onPendingRespondIds?.(ids, { hasClarificationRespond });
  }, [
    clauseResponses,
    onPendingRespondIds,
    isClauseResponsesLoading,
    isPendingThread,
  ]);

  const handleDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const toggleClauseExpand = clauseId => {
    setExpandedClauses(prev => {
      const list = toClauseResponseList(clauseResponses[clauseId]);
      const defaultExpanded = list.some(item =>
        hasClauseResponse(item?.respondNotes)
      );
      return {
        ...prev,
        [clauseId]: !(prev[clauseId] ?? defaultExpanded),
      };
    });
  };

  const handleClauseSave = async clauseId => {
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!respondNotes) {
      notify.warning('Please enter a response note.');
      return;
    }

    try {
      const saved = await saveClauseResponse(clauseId, respondNotes);
      if (!saved) {
        notify.error('Failed to save response. Please try again.');
        return;
      }
      const syncedNotes = saved?.respondNotes ?? respondNotes;
      setExpandedClauses(prev => ({ ...prev, [clauseId]: true }));
      setDraftNotes(prev => ({ ...prev, [clauseId]: syncedNotes }));
      notify.success('Response saved successfully');
    } catch {
      notify.error('Failed to save response. Please try again.');
    }
  };

  const handleClauseCancel = clauseId => {
    const saved = isEvaluatorView
      ? (getOwnClauseResponse(clauseResponses[clauseId], {
          isEvaluator: true,
        })?.respondNotes ?? '')
      : '';
    setDraftNotes(prev => ({ ...prev, [clauseId]: saved }));
    if (!hasClauseResponse(saved)) {
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
    }
  };

  const handleClauseClear = async clauseId => {
    try {
      await deleteClauseResponse(clauseId);
      setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
      const ownResponse = getOwnClauseResponse(clauseResponses[clauseId], {
        isEvaluator: isEvaluatorView,
      });
      const hasRemaining = toClauseResponseList(clauseResponses[clauseId])
        .filter(item => item?.id !== ownResponse?.id)
        .some(item => hasClauseResponse(item?.respondNotes));
      setExpandedClauses(prev => ({ ...prev, [clauseId]: hasRemaining }));
      notify.success('Response removed successfully');
    } catch {
      notify.error('Failed to remove response. Please try again.');
    }
  };

  const handleClauseSecondaryAction = (clauseId, hasSaved) => {
    if (hasSaved) {
      handleClauseClear(clauseId);
    } else {
      handleClauseCancel(clauseId);
    }
  };

  return (
    <SectionCard>
      <SectionHeader
        title='Clause Details'
        subtitle='Review procurement clauses and provide clarification responses'
        countLabel={
          confirmationStats.total > 0
            ? `${confirmationStats.confirmedCount}/${confirmationStats.total} confirmed`
            : `${visibleClauses.length} clauses`
        }
      />

      {confirmationStats.total > 0 ? (
        <div className='mb-3 px-1'>
          <div className='text-muted fs-12 fw-medium mb-1'>
            Confirmation status
          </div>
          <div className='text-dark fs-13'>
            {confirmationStats.confirmedCount}/{confirmationStats.total}{' '}
            confirmed
            <span className='text-muted'>
              {' '}
              · Mandatory {confirmationStats.mandatoryConfirmedCount}/
              {confirmationStats.mandatoryTotal} · Optional{' '}
              {confirmationStats.optionalConfirmedCount}/
              {confirmationStats.optionalTotal}
            </span>
          </div>
        </div>
      ) : null}

      <SectionAlert title='Specification Compliance Notice'>
        <div className='text-muted small'>
          Any information that does not align with Pertamina&apos;s established
          standards may be subject to further evaluation and acceptance at
          Pertamina&apos;s discretion.
        </div>
      </SectionAlert>

      {visibleClauses.length > 0 ? (
        <Row>
          {visibleClauses.map((clause, index) => (
            <ClauseCard
              key={clause.id}
              index={index + 1}
              clause={clause}
              clauseResponses={clauseResponses}
              draftNotes={draftNotes}
              expandedClauses={expandedClauses}
              formMain={EVALUATOR_FORM_MAIN}
              isClauseResponsesSaving={isClauseResponsesSaving}
              isClauseResponsesLoading={isClauseResponsesLoading}
              isEvaluatorView={isEvaluatorView}
              handleDraftChange={handleDraftChange}
              handleClauseSecondaryAction={handleClauseSecondaryAction}
              handleClauseSave={handleClauseSave}
              toggleClauseExpand={toggleClauseExpand}
              onLockResponse={
                isEvaluatorView && !isRespondClosed
                  ? handleLockResponse
                  : undefined
              }
              isRespondClosed={isRespondClosed}
              anchorId={`ic-respond-clause-${clause.id}`}
              finalValuePrefill={clause.clauseDescription ?? ''}
              highlightPending={
                highlightArmed && isPendingThread(clauseResponses[clause.id])
              }
            />
          ))}
        </Row>
      ) : (
        <EmptyState
          compact
          icon='ri-file-list-3-line'
          title='No clauses to clarify'
          emptyHint='This procurement has no clauses visible to bidders, so there is nothing to clarify.'
        />
      )}
    </SectionCard>
  );
};

export default ClauseDetailSection;
